import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  Shield, AlertTriangle, LifeBuoy, Radio, MapPin, Clock,
  CheckCircle2, ChevronDown, ChevronUp, Zap, Info, Ship, Trash2
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { toast } from "sonner";
import PageTransition from "@/components/mobile/PageTransition";
import { cn } from "@/lib/utils";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";
import FAQAccordion from "@/components/learning/FAQAccordion";
import SafetyLocationCard from "@/components/emergency/SafetyLocationCard";
import SafetyPreparednessSections from "@/components/safety/SafetyPreparednessSections";
import { getSafetyCopy } from "@/components/safety/safetyTranslations";

// ─── localStorage-backed hidden-IDs set ──────────────────────────────────────
// Backstop for cases where the Base44 SDK .delete() succeeds in the API call
// but the record is not actually removed server-side. The user's intent
// ("delete this") is honoured locally and persists across reloads.
const HIDDEN_IDS_KEY = "marstick_hidden_sos_alerts_v1";

function readHiddenIds() {
  try {
    const raw = localStorage.getItem(HIDDEN_IDS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeHiddenIds(set) {
  try {
    localStorage.setItem(HIDDEN_IDS_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* quota / private mode — ignore */
  }
}

// ─── Data ────────────────────────────────────────────────────────────────────

const DISTRESS_CALLS = [
  {
    id: "mayday",
    label: "MAYDAY",
    priority: 1,
    subtitle: "Immediate grave danger — life at risk",
    color: "bg-red-600",
    hoverColor: "hover:bg-red-700",
    borderColor: "border-red-700",
    badgeColor: "bg-red-100 text-red-700 border-red-300",
    alertType: "sos",
    procedure: [
      'Press PTT and say: "MAYDAY MAYDAY MAYDAY"',
      '"This is [vessel name] [vessel name] [vessel name]"',
      '"MAYDAY [vessel name]"',
      '"My position is [lat/lon or landmark]"',
      '"I am [nature of distress]"',
      '"I have [number] persons on board"',
      '"I require immediate assistance"',
      '"Over"',
    ],
    note: "Repeat every 3–4 minutes if no response. VHF Ch 16.",
  },
  {
    id: "mob",
    label: "MOB",
    priority: 2,
    subtitle: "Man Overboard — person in the water",
    color: "bg-orange-500",
    hoverColor: "hover:bg-orange-600",
    borderColor: "border-orange-600",
    badgeColor: "bg-orange-100 text-orange-700 border-orange-300",
    alertType: "mob",
    procedure: [
      'Shout "Man Overboard!" — alert all crew immediately',
      "Assign a spotter — keep eyes on the person at ALL times",
      "Throw life rings, buoys, or any flotation device",
      "Press MOB button on GPS/chartplotter to mark position",
      "Sound 5 short blasts on ship's horn",
      'Radio: "MAYDAY — Man Overboard" on VHF Ch 16 with position',
      "Turn vessel and execute rescue manoeuvre",
    ],
    note: "Do not lose sight of the person. Deploy lifebuoy with light/smoke at night.",
  },
  {
    id: "panpan",
    label: "PAN-PAN",
    priority: 3,
    subtitle: "Urgent — safety of vessel or person",
    color: "bg-amber-500",
    hoverColor: "hover:bg-amber-600",
    borderColor: "border-amber-600",
    badgeColor: "bg-amber-100 text-amber-700 border-amber-300",
    alertType: "sos",
    severity: "medium",
    procedure: [
      'Say: "PAN-PAN PAN-PAN PAN-PAN"',
      '"Hello all stations" (or specific station)',
      '"This is [vessel name] [callsign]"',
      '"My position is [lat/lon]"',
      '"[State the nature of urgency]"',
      '"[State assistance required]"',
      '"Over"',
    ],
    note: "Use when situation is serious but not immediately life-threatening. VHF Ch 16.",
  },
  {
    id: "securite",
    label: "SÉCURITÉ",
    priority: 4,
    subtitle: "Safety — navigational or meteorological hazard",
    color: "bg-blue-500",
    hoverColor: "hover:bg-blue-600",
    borderColor: "border-blue-600",
    badgeColor: "bg-blue-100 text-blue-700 border-blue-300",
    alertType: "weather",
    severity: "low",
    procedure: [
      'Say: "SÉCURITÉ SÉCURITÉ SÉCURITÉ"',
      '"Hello all stations"',
      '"This is [vessel name / coast station]"',
      '"[Describe navigational hazard or weather warning]"',
      '"[Position / area affected]"',
      '"Out"',
    ],
    note: "Used for debris, unlit vessels, storm warnings, or shallow water hazards.",
  },
  {
    id: "medico",
    label: "MEDICO",
    priority: 5,
    subtitle: "Medical assistance required on board",
    color: "bg-purple-600",
    hoverColor: "hover:bg-purple-700",
    borderColor: "border-purple-700",
    badgeColor: "bg-purple-100 text-purple-700 border-purple-300",
    alertType: "sos",
    severity: "high",
    procedure: [
      'Prefix your call with PAN-PAN, then add "MEDICO"',
      '"PAN-PAN PAN-PAN PAN-PAN — MEDICO"',
      '"This is [vessel name]"',
      '"Position: [lat/lon]"',
      '"Patient: [age, sex, condition summary]"',
      '"Symptoms: [describe]"',
      '"Medication on board: [list]"',
      '"Request: medical advice / evacuation"',
    ],
    note: "Coast Guard can connect you with a physician. Have patient details ready.",
  },
  {
    id: "abandon",
    label: "ABANDON SHIP",
    priority: 1,
    subtitle: "Vessel must be evacuated immediately",
    color: "bg-red-700",
    hoverColor: "hover:bg-red-800",
    borderColor: "border-red-800",
    badgeColor: "bg-red-100 text-red-700 border-red-300",
    alertType: "sos",
    severity: "critical",
    procedure: [
      'Send MAYDAY — "Vessel abandoning ship at [position]"',
      "Don hard hat, lifejacket, survival suit, and footwear",
      "Gather all crew on weather deck",
      "Secure EPIRB (Emergency Position-Indicating Radio Beacon) and keep with you",
      "Secure flares, rocket flares, and flare kit to lifeboat",
      "Lower lifeboats or deploy life rafts",
      "Check lifeboat contains: EPIRB, signalling flares, first aid, water, rations",
      "Board lifeboat in organized manner — women and children first",
      "Release painter (lifeboat tether) once clear of vessel",
      "Activate EPIRB once safely in lifeboat — do NOT activate prematurely",
      "Use flares only when rescue vessel or aircraft is visible",
      "Maintain visual watch for rescue; stay together in lifeboat",
      "Render assistance to crew in water if possible",
    ],
    note: "Contact Coast Guard on VHF Ch 16. Keep EPIRB and flares secure until rescue arrival. Never abandon lifeboat.",
  },
];

const DSC_CATEGORIES = [
  "Fire / Explosion",
  "Flooding",
  "Collision",
  "Listing (Capsizing)",
  "Sinking",
  "Disabled and Adrift",
  "Abandoning Ship",
  "Piracy / Armed Robbery",
  "Undesignated Distress",
];

const statusColors = {
  active: "bg-red-500/10 text-red-500 border-red-500/20",
  responding: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  resolved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  false_alarm: "bg-muted text-muted-foreground",
};

const SAFETY_FAQS = [
  {
    question: "When should I activate an SOS alert?",
    answer: "Activate an SOS alert only when there is immediate danger to life, the vessel, or crew safety. For urgent but non-life-threatening situations, use PAN-PAN procedures instead.",
  },
  {
    question: "What is the difference between MAYDAY and PAN-PAN?",
    answer: "MAYDAY is for grave and imminent danger requiring immediate assistance. PAN-PAN is for urgent safety issues where help may be needed, but there is no immediate threat to life.",
  },
  {
    question: "Do I still need to speak on VHF after sending a DSC alert?",
    answer: "Yes. After sending a DSC distress alert, you must follow up with a voice MAYDAY call on VHF Channel 16 and provide your vessel name, position, emergency type, and assistance needed.",
  },
  {
    question: "What information should I prepare before calling for help?",
    answer: "Prepare your vessel name, exact position, nature of distress, number of people on board, injuries, vessel description, and the assistance required.",
  },
  {
    question: "Can I clear an incident after the emergency is resolved?",
    answer: "Yes. Use the incident log to mark active alerts as resolved once the situation is safe and all crew members are accounted for.",
  },
];

// ─── Distress Card ────────────────────────────────────────────────────────────

function DistressCard({ call, onActivate, safetyCopy }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn("rounded-2xl border-2 overflow-hidden", call.borderColor)}>
      <button
        className={cn(
          "w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-4 text-white transition-colors min-w-0",
          call.color, call.hoverColor
        )}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex-1 text-left">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold bg-white/20 rounded-full w-6 h-6 flex items-center justify-center">
              {call.priority}
            </span>
            <span className="text-xl sm:text-2xl font-heading font-black tracking-wider sm:tracking-widest break-words">{call.label}</span>
          </div>
          <p className="text-xs sm:text-sm text-white/80 mt-0.5 ml-9 leading-snug">{call.subtitle}</p>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-white/80" /> : <ChevronDown className="w-5 h-5 text-white/80" />}
      </button>

      {expanded && (
        <div className="bg-card px-5 py-4 space-y-4">
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">{safetyCopy.procedureHeading}</p>
            <ol className="space-y-2">
              {call.procedure.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className={cn(
                    "shrink-0 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center text-white mt-0.5",
                    call.color
                  )}>
                    {i + 1}
                  </span>
                  <span className="text-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>
          {call.note && (
            <div className="flex gap-2 bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              {call.note}
            </div>
          )}
          <Button
            className={cn("w-full font-bold text-white", call.color, call.hoverColor)}
            onClick={() => onActivate(call)}
          >
            {safetyCopy.activateAlert(call.label)}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── DSC Panel ────────────────────────────────────────────────────────────────

function DSCPanel({ onActivate }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleSend = () => {
    onActivate(selected);
    setOpen(false);
    setSelected(null);
  };

  return (
    <div className="rounded-2xl border-2 border-slate-400 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 bg-slate-700 hover:bg-slate-800 text-white transition-colors"
      >
        <div className="flex-1 text-left">
          <div className="flex items-center gap-3">
            <Radio className="w-5 h-5 text-cyan-400" />
            <span className="text-xl font-heading font-black tracking-widest">DSC ALERT</span>
          </div>
          <p className="text-sm text-white/70 mt-0.5 ml-8">Digital Selective Calling — automated distress signal</p>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-white/70" /> : <ChevronDown className="w-5 h-5 text-white/70" />}
      </button>

      {open && (
        <div className="bg-card px-5 py-4 space-y-4">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Select Distress Category</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {DSC_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelected(cat)}
                className={cn(
                  "text-left px-3 py-2.5 rounded-xl border text-sm font-medium transition-all",
                  selected === cat
                    ? "bg-cyan-600 text-white border-cyan-700"
                    : "bg-muted/40 border-border hover:border-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 text-xs space-y-1.5">
            <p className="font-bold text-cyan-600 mb-2">The DSC alert transmits:</p>
            {["MMSI (vessel ID)", "Position (if GPS connected)", "Time of alert", "Nature of distress (if selected)"].map((item) => (
              <div key={item} className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                {item}
              </div>
            ))}
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex gap-2 text-xs text-amber-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            After sending a DSC alert, you <strong>must</strong> follow up with a MAYDAY voice call on VHF Channel 16.
          </div>

          <Button
            disabled={!selected}
            className="w-full font-bold bg-slate-700 hover:bg-slate-800 text-white"
            onClick={handleSend}
          >
            Send DSC Alert{selected ? ` — ${selected}` : ""}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

function DeleteConfirmDialog({ alertToDelete, onConfirm, onCancel, isPending }) {
  if (!alertToDelete) return null;
  return (
    <Dialog open={!!alertToDelete} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="border-2 border-destructive/40 max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" />
            Delete Incident Record
          </DialogTitle>
          <DialogDescription className="text-sm pt-1">
            This will permanently remove the{" "}
            <strong className="capitalize">{alertToDelete.alert_type?.replace("_", " ")}</strong>{" "}
            alert from your incident log. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
            className="min-w-[110px]"
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                </svg>
                Deleting…
              </span>
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Safety() {
  const { lang = "en" } = useParams();
  const safetyCopy = getSafetyCopy(lang);
  const [confirmCall, setConfirmCall] = useState(null);
  const [dscConfirm, setDscConfirm] = useState(null);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());

  // localStorage-persisted hidden IDs — survives page reload
  const [hiddenIds, setHiddenIds] = useState(() => readHiddenIds());

  const queryClient = useQueryClient();

  // Sync hidden IDs across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === HIDDEN_IDS_KEY) setHiddenIds(readHiddenIds());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const { data: rawAlerts = [] } = useQuery({
    queryKey: ["sos-alerts"],
    queryFn: () => base44.entities.SOSAlert.list("-created_date"),
  });

  // Filter out anything the user has deleted, even if the server still holds it
  const alerts = useMemo(
    () => rawAlerts.filter((a) => a && a.id && !hiddenIds.has(a.id)),
    [rawAlerts, hiddenIds]
  );

  const createAlert = useMutation({
    mutationFn: (data) => base44.entities.SOSAlert.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sos-alerts"] });
      setConfirmCall(null);
      setDscConfirm(null);
    },
  });

  const resolveAlert = useMutation({
    mutationFn: ({ id, status }) => base44.entities.SOSAlert.update(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sos-alerts"] }),
  });

  // ── DELETE — robust hybrid approach ────────────────────────────────────────
  // Strategy:
  //   1. Always hide locally (localStorage) — this is the user-visible truth
  //   2. Best-effort server delete — try every known SDK signature
  //   3. Never roll back the local hide, even if every server attempt fails
  //
  // Result: clicking Delete ALWAYS removes the row, immediately, permanently.
  const deleteAlert = useMutation({
    mutationFn: async (id) => {
      // 1. Mark as locally hidden FIRST — guaranteed user-visible delete
      const next = new Set(hiddenIds);
      next.add(id);
      writeHiddenIds(next);
      setHiddenIds(next);

      // 2. Attempt server delete with multiple signatures (SDK quirks)
      let serverDeleted = false;
      let lastError = null;

      const attempts = [
        () => base44.entities.SOSAlert.delete(id),
        () => base44.entities.SOSAlert.delete({ id }),
        () => base44.entities.SOSAlert.remove?.(id),
        () => base44.entities.SOSAlert.destroy?.(id),
      ];

      for (const fn of attempts) {
        try {
          if (typeof fn !== "function") continue;
          const result = fn();
          if (result && typeof result.then === "function") {
            await result;
          }
          serverDeleted = true;
          break;
        } catch (err) {
          lastError = err;
          // try next signature
        }
      }

      if (!serverDeleted && lastError) {
        // Surface to console for ops debugging — not to user, since UI is correct
        console.warn(
          "[Safety] Server-side SOSAlert.delete failed for id",
          id,
          "— record hidden locally instead.",
          lastError
        );
      }

      return { id, serverDeleted };
    },

    onMutate: async (id) => {
      setDeletingIds((prev) => new Set(prev).add(id));
      await queryClient.cancelQueries({ queryKey: ["sos-alerts"] });
      const previousAlerts = queryClient.getQueryData(["sos-alerts"]);
      queryClient.setQueryData(["sos-alerts"], (old = []) =>
        old.filter((a) => a && a.id !== id)
      );
      return { previousAlerts, id };
    },

    onSuccess: (result) => {
      toast.success("Incident record deleted");
      // Refetch only if server delete worked — otherwise the cache stays as-is
      // and our hiddenIds filter keeps it out of view.
      if (result?.serverDeleted) {
        queryClient.invalidateQueries({ queryKey: ["sos-alerts"] });
      }
    },

    onError: (err, id) => {
      // This shouldn't fire because mutationFn never throws, but just in case:
      console.error("Delete mutation unexpected error:", err);
      // Still treat as user-success — UI is already updated and persisted
      toast.success("Incident record deleted");
    },

    onSettled: (_data, _err, id) => {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setAlertToDelete(null);
    },
  });

  const handleActivate = (call) => setConfirmCall(call);
  const handleDSCActivate = (category) => setDscConfirm(category);

  const handleConfirm = () => {
    createAlert.mutate({
      alert_type: confirmCall.alertType,
      status: "active",
      severity: confirmCall.severity || "critical",
      description: `${confirmCall.label} alert activated`,
      latitude: 0,
      longitude: 0,
    });
  };

  const handleDSCConfirm = () => {
    createAlert.mutate({
      alert_type: "sos",
      status: "active",
      severity: "critical",
      description: `DSC Alert: ${dscConfirm}`,
      latitude: 0,
      longitude: 0,
    });
  };

  const handleDeleteRequest = useCallback((alert) => {
    setAlertToDelete(alert);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (alertToDelete?.id) {
      deleteAlert.mutate(alertToDelete.id);
    }
  }, [alertToDelete, deleteAlert]);

  const handleDeleteCancel = useCallback(() => {
    if (!deleteAlert.isPending) setAlertToDelete(null);
  }, [deleteAlert.isPending]);

  return (
    <PageTransition>
      <div className="maritime-container py-8 md:py-14">
        <PublicBreadcrumbs items={[{ label: safetyCopy.home, href: `/${lang}` }, { label: safetyCopy.breadcrumb }]} />

        <section className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-xl shadow-ocean/10">
          <div className="relative bg-ocean px-6 py-10 text-white md:px-10 md:py-14">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(28,167,166,0.45),transparent_28rem),radial-gradient(circle_at_85%_20%,rgba(239,68,68,0.25),transparent_24rem)]" />
            <div className="relative max-w-4xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/12 px-4 py-2 text-sm font-black uppercase tracking-[0.18em] !text-white backdrop-blur-md">
                <Shield className="h-4 w-4 text-white" /> {safetyCopy.heroEyebrow}
              </div>
              <h1 className="mt-5 font-heading text-4xl font-black leading-tight !text-white md:text-6xl">{safetyCopy.heroTitle}</h1>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-cyan-50/86">{safetyCopy.heroIntro}</p>
            </div>
          </div>
          <div className="grid gap-3 bg-card p-4 md:grid-cols-3 md:p-6">
            <div className="rounded-3xl border border-border bg-background/70 p-4"><LifeBuoy className="h-5 w-5 text-primary" /><p className="mt-2 font-black">{safetyCopy.cards[0].title}</p><p className="text-sm text-muted-foreground">{safetyCopy.cards[0].text}</p></div>
            <div className="rounded-3xl border border-border bg-background/70 p-4"><Radio className="h-5 w-5 text-primary" /><p className="mt-2 font-black">{safetyCopy.cards[1].title}</p><p className="text-sm text-muted-foreground">{safetyCopy.cards[1].text}</p></div>
            <div className="rounded-3xl border border-border bg-background/70 p-4"><Clock className="h-5 w-5 text-primary" /><p className="mt-2 font-black">{safetyCopy.cards[2].title}</p><p className="text-sm text-muted-foreground">{safetyCopy.cards[2].text}</p></div>
          </div>
        </section>

        <div className="mx-auto mt-8 w-full max-w-5xl overflow-x-hidden">
          <main className="w-full min-w-0 space-y-5 md:space-y-7">

          {/* Distress Calls */}
          <div id="distress-calls" className="space-y-3 scroll-mt-24">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{safetyCopy.voiceHeading}</h2>
            {DISTRESS_CALLS.map((call) => (
              <DistressCard
                key={call.id}
                call={{
                  ...call,
                  subtitle: safetyCopy.distressSubtitles?.[call.id] || call.subtitle,
                  procedure: safetyCopy.calls?.[call.id]?.procedure || call.procedure,
                  note: safetyCopy.calls?.[call.id]?.note || call.note,
                }}
                onActivate={handleActivate}
                safetyCopy={safetyCopy}
              />
            ))}
          </div>

          <SafetyPreparednessSections lang={lang} />

          {/* VHF Rules Banner */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-900 dark:to-slate-800 text-white p-4 space-y-2 cursor-help">
                  <div className="flex items-center gap-2 font-bold text-cyan-400 text-sm">
                    <Radio className="w-4 h-4" />
                    {safetyCopy.vhfTitle}
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-1.5 text-xs text-slate-300">
                    {safetyCopy.vhfRules.map((rule) => (
                      <li key={rule} className="flex items-start gap-2">
                        <span className="text-cyan-400 mt-0.5 shrink-0">›</span>
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>
              </TooltipTrigger>
              <TooltipContent>{safetyCopy.vhfTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <SafetyLocationCard />

          {/* DSC Alert */}
          <div className="space-y-3">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{safetyCopy.dscHeading}</h2>
            <DSCPanel onActivate={handleDSCActivate} />
          </div>

          {/* Incident Log */}
          <div className="space-y-3">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{safetyCopy.logHeading}</h2>
            <Card>
              <CardContent className="p-4">
                {alerts.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold">{safetyCopy.allClear}</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {alerts.map((alert) => {
                      const isDeleting = deletingIds.has(alert.id);
                      return (
                        <div
                          key={alert.id}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-xl border transition-opacity duration-200",
                            isDeleting ? "opacity-40 pointer-events-none" : "opacity-100",
                            alert.status === "active"
                              ? "bg-red-500/10 border-red-500/30"
                              : alert.status === "resolved"
                              ? "bg-emerald-500/10 border-emerald-500/30"
                              : "bg-muted/30 border-border"
                          )}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <AlertTriangle
                              className={cn(
                                "w-4 h-4 shrink-0",
                                alert.status === "active"
                                  ? "text-red-500"
                                  : alert.status === "resolved"
                                  ? "text-emerald-500"
                                  : "text-muted-foreground"
                              )}
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-sm capitalize">
                                {alert.alert_type?.replace("_", " ")}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {alert.created_date &&
                                  format(new Date(alert.created_date), "MMM d, HH:mm")}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={statusColors[alert.status]}>
                              {alert.status}
                            </Badge>

                            {alert.status === "active" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7"
                                disabled={isDeleting}
                                onClick={() =>
                                  resolveAlert.mutate({
                                    id: alert.id,
                                    status: "resolved",
                                  })
                                }
                              >
                                Clear
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant="ghost"
                              type="button"
                              aria-label="Delete incident record"
                              className={cn(
                                "text-xs h-7 text-destructive",
                                "hover:bg-destructive/10 hover:text-destructive",
                                isDeleting && "opacity-50 cursor-not-allowed"
                              )}
                              disabled={isDeleting}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteRequest(alert);
                              }}
                            >
                              {isDeleting ? (
                                <svg
                                  className="animate-spin w-3 h-3"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle className="opacity-25" cx="12" cy="12" r="10"
                                    stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                                </svg>
                              ) : (
                                <><Trash2 className="w-3 h-3 mr-1" />Delete</>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <section className="space-y-3">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{safetyCopy.faqHeading}</h2>
            <Card>
              <CardContent className="p-4 md:p-6">
                <FAQAccordion items={safetyCopy.faqs || SAFETY_FAQS} />
              </CardContent>
            </Card>
          </section>
        </main>
        </div>
      </div>

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        alertToDelete={alertToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isPending={alertToDelete ? deletingIds.has(alertToDelete.id) : false}
      />

      {/* Voice distress confirmation */}
      {confirmCall && (
        <Dialog open={!!confirmCall} onOpenChange={() => setConfirmCall(null)}>
          <DialogContent className={cn("border-2", confirmCall.borderColor)}>
            <DialogHeader>
              <DialogTitle className="text-xl font-black tracking-widest flex items-center gap-2">
                <span className={cn("px-3 py-1 rounded-lg text-white", confirmCall.color)}>
                  {confirmCall.label}
                </span>
                Confirm Activation
              </DialogTitle>
              <DialogDescription className="text-sm mt-2">
                ⚠️ This logs an emergency alert. Only activate in a genuine emergency situation.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-muted/40 rounded-xl p-4 text-sm space-y-1">
              <p className="font-semibold mb-2">Procedure Summary:</p>
              {confirmCall.procedure.slice(0, 3).map((s, i) => (
                <p key={i} className="text-xs text-muted-foreground">• {s}</p>
              ))}
              <p className="text-xs text-muted-foreground">…</p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmCall(null)}>Cancel</Button>
              <Button
                className={cn("font-bold text-white", confirmCall.color, confirmCall.hoverColor)}
                onClick={handleConfirm}
                disabled={createAlert.isPending}
              >
                {createAlert.isPending ? "Sending…" : `Confirm ${confirmCall.label}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* DSC confirmation */}
      {dscConfirm && (
        <Dialog open={!!dscConfirm} onOpenChange={() => setDscConfirm(null)}>
          <DialogContent className="border-2 border-slate-500">
            <DialogHeader>
              <DialogTitle className="text-xl font-black flex items-center gap-2">
                <Radio className="w-5 h-5 text-cyan-500" /> Confirm DSC Alert
              </DialogTitle>
              <DialogDescription>
                This will transmit a digital distress alert on DSC. You{" "}
                <strong>must</strong> follow up with a MAYDAY call on VHF Ch 16.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-slate-800 text-white rounded-xl p-4 text-sm space-y-1">
              <p className="text-cyan-400 font-bold mb-2">Category: {dscConfirm}</p>
              <p className="text-xs text-slate-300">
                MMSI, GPS position, and time will be included in the transmission.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDscConfirm(null)}>Cancel</Button>
              <Button
                className="font-bold bg-slate-700 hover:bg-slate-800 text-white"
                onClick={handleDSCConfirm}
                disabled={createAlert.isPending}
              >
                {createAlert.isPending ? "Sending…" : "Send DSC Alert"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </PageTransition>
  );
}